var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc demand chart
 */
import { createRef } from '@byted/light-render';
import { interpolateColor } from '@byted/light-render/lib/interpolate';
import * as lodash from '../../utils/lodash';
import { Group, Circle } from '../../element';
import { createRichText } from '../../rich';
import { SeriesModel } from '../../model';
import { interpolateNumber, getPercentOrNumberValue, isPercent } from '../../utils/math';
import { getRectLabelPosition } from '../../utils/textLayout';
import { registerChart } from '../index';
import { collisionTest } from './demandHelper';
import measureText from '../../utils/measureText';
/**
 * default
 */
var DemandModel = /** @class */ (function (_super) {
    __extends(DemandModel, _super);
    function DemandModel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'demand';
        return _this;
    }
    DemandModel.prototype.getDefaultOption = function () {
        return {
            type: 'demand',
            name: '',
            center: ['50%', '50%'],
            radiusRange: [100, '40%'],
            encode: {
                radius: null,
                size: 12,
                name: null,
                color: null,
            },
            label: {
                show: true,
                position: 'bottom',
                distance: 8,
                fontSize: 12,
                color: '#333333',
                formatter: function (point) { return point.name; },
            },
            shapeStyle: {
                fill: null,
                lineWidth: 0,
                stroke: null,
                cursor: 'pointer',
            },
            keyword: '',
            keywordRadius: 60,
            keywordColor: null,
            avoidOverlap: false,
            rippleRange: ['22%', '34%', '45%'],
            scale: 'log',
            keywordStyle: {
                stroke: null,
                lineWidth: 0,
                cursor: 'pointer',
            },
            keywordTextStyle: {
                color: '#ffffff',
                fontSize: 18,
                textAlign: 'center',
                textBaseline: 'middle',
                pointerEvents: 'none',
            },
            rippleStyle: {
                colors: ['rgba(47, 136, 255, 0.05)'],
                fill: null,
                lineWidth: 1,
                stroke: null,
            },
            hover: {
                keywordStyle: {
                    fill: null,
                    stroke: null,
                    lineWidth: null,
                },
                shapeStyle: {
                    fill: null,
                    stroke: null,
                    lineWidth: null,
                },
                label: {
                    show: null,
                    fontSize: null,
                    color: null,
                },
            },
            visible: true,
            showLegend: false,
        };
    };
    DemandModel.prototype.getInitialState = function () {
        return {
            cx: 0,
            cy: 0,
            minRadius: 0,
            maxRadius: 0,
            points: [],
            needAnimation: this.needAnimation(),
        };
    };
    DemandModel.prototype.toggleHighlightPoint = function (dataIndex, active) {
        this.state.points[dataIndex].shapeRef.current.stopAllAnimation().animateTo(__assign({}, this.getPointAttr(this.state.points[dataIndex], { hover: active })));
    };
    DemandModel.prototype.getScaleValue = function (val) {
        return this.option.scale === 'linear' ? val : 1 / Math.log(val);
    };
    DemandModel.prototype.beforeRender = function () {
        var _this = this;
        var _a = this.getFacetBBox(), top = _a.top, left = _a.left, width = _a.width, height = _a.height;
        var _b = this.option, center = _b.center, radiusRange = _b.radiusRange, label = _b.label;
        var radiusExtreme = this._dataset.getExtreme(this.option.encode.radius);
        var minValue = this.getScaleValue(radiusExtreme[0]);
        var maxValue = this.getScaleValue(radiusExtreme[1]);
        var cx = (isPercent(center[0]) ? left + getPercentOrNumberValue(center[0], width) : center[0]);
        var cy = (isPercent(center[1]) ? top + getPercentOrNumberValue(center[1], height) : center[1]);
        var minRadius = getPercentOrNumberValue(radiusRange[0], width);
        var maxRadius = getPercentOrNumberValue(radiusRange[1], width);
        var sizeField = lodash.isArray(this.option.encode.size) ? this.option.encode.size[0] : null;
        var points = [];
        this._dataset.eachData(function (record, dataIndex) {
            var encode = _this._encode.getEncodeData(record, dataIndex);
            var radius = encode.radius, name = encode.name, color = encode.color;
            var distance = interpolateNumber(_this.getScaleValue(radius), [minValue, maxValue], [maxRadius, minRadius]);
            var yflag = dataIndex % 2 === 0 ? 1 : -1;
            var xflag = dataIndex % 4 < 2 ? 1 : -1;
            var maxOffsetY = Math.min(height, distance * 2) / 2;
            var maxAngle = Math.asin(maxOffsetY / distance);
            var angleList = [];
            var searchSteps = 60;
            var step = maxAngle / searchSteps;
            for (var i = 0; i < searchSteps; i++) {
                angleList.push(i * step);
            }
            angleList = lodash.shuffle(angleList);
            var textWidth = measureText(name, label).width;
            var offsetY;
            var angle;
            var plotX;
            var plotY;
            var flag = false;
            for (var _i = 0, angleList_1 = angleList; _i < angleList_1.length; _i++) {
                var stepAngle = angleList_1[_i];
                angle = stepAngle;
                offsetY = Math.sin(angle) * distance;
                plotX = cx + distance * Math.cos(angle) * xflag;
                plotY = cy + offsetY * yflag;
                var passed = points.every(function (prevPoint) {
                    return !collisionTest({
                        cx: plotX,
                        cy: plotY,
                        size: encode.size,
                        textWidth: textWidth,
                        position: label.position,
                        distance: label.distance,
                        fontSize: label.fontSize,
                    }, {
                        cx: prevPoint.plotX,
                        cy: prevPoint.plotY,
                        size: prevPoint.encode.size,
                        textWidth: prevPoint.textWidth,
                        position: label.position,
                        distance: label.distance,
                        fontSize: label.fontSize,
                    }, cx);
                });
                if (passed) {
                    flag = true;
                    break;
                }
            }
            var point = {
                encode: encode,
                name: name,
                color: color,
                value: sizeField ? record[sizeField] : '',
                plotX: plotX,
                plotY: plotY,
                group: 0,
                textWidth: textWidth,
                visible: _this.option.avoidOverlap ? flag : true,
                shapeRef: createRef(),
            };
            points.push(point);
        });
        this.setState({
            cx: cx,
            cy: cy,
            minRadius: minRadius,
            maxRadius: maxRadius,
            points: points,
        });
    };
    DemandModel.prototype.afterRender = function () {
        var _this = this;
        if (!this.state.needAnimation) {
            return;
        }
        this.state.points.forEach(function (point, dataIndex) {
            var attr = _this.getPointAttr(_this.state.points[dataIndex], { needAnimation: false });
            point.shapeRef.current.animateTo(attr, 400);
        });
        this.setState({ needAnimation: false });
    };
    DemandModel.prototype.getPointAttr = function (point, state) {
        if (state === void 0) { state = {}; }
        var plotX = point.plotX, plotY = point.plotY, encode = point.encode, color = point.color;
        var size = state.needAnimation ? 0 : encode.size;
        return __assign({ cx: plotX, cy: plotY, radius: size / 2, fill: color, stroke: color }, this.getShapeStyle(state.hover ? this.option.hover.shapeStyle : this.option.shapeStyle));
    };
    DemandModel.prototype.getLabelAttr = function (point) {
        var plotX = point.plotX, plotY = point.plotY, encode = point.encode;
        var _a = this.option.label, position = _a.position, distance = _a.distance;
        var size = encode.size;
        var textPosition = getRectLabelPosition({
            top: plotY - size / 2,
            left: plotX - size / 2,
            right: plotX + size / 2,
            bottom: plotY + size / 2,
        }, position, distance, plotX > this.state.cx ? 'right' : 'left');
        return __assign(__assign(__assign({}, textPosition), { fill: '#333333', pointerEvents: 'none' }), this.getTextStyle(this.option.label));
    };
    DemandModel.prototype.renderRipples = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('ripples'),
        });
        var rippleRange = this.option.rippleRange;
        var colors = this.option.rippleStyle.colors;
        rippleRange
            .sort()
            .reverse()
            .forEach(function (rippleRadius, index) {
            var rippleIndex = rippleRange.length - 1 - index;
            var color;
            if (lodash.isArray(colors)) {
                color = colors[(rippleIndex - 1) % colors.length];
            }
            else if (lodash.isString(colors)) {
                var _a = colors.split('-'), startColor = _a[0], endColor = _a[1];
                var progress = rippleIndex / (rippleRange.length - 1);
                color = interpolateColor(startColor, endColor, progress);
            }
            group.add(new Circle(__assign({ cx: _this.state.cx, cy: _this.state.cy, radius: getPercentOrNumberValue(rippleRadius, _this.getFacetBBox().width), fill: color }, _this.getShapeStyle(_this.option.rippleStyle))));
        });
        return group;
    };
    DemandModel.prototype.renderPoints = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('points'),
        });
        this.state.points.forEach(function (point, dataIndex) {
            var circle = new Circle(__assign(__assign({ key: point.name, ref: point.shapeRef, display: point.visible }, _this.getPointAttr(point, { needAnimation: _this.state.needAnimation })), { onEvent: function (event) {
                    return _this.handleUIEvent(event, 'point', {
                        title: _this.option.name,
                        point: _this.getWrappedPoint(dataIndex),
                    });
                } }));
            group.add(circle);
        });
        return group;
    };
    DemandModel.prototype.renderLabels = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('labels'),
        });
        this.state.points.map(function (point, dataIndex) {
            var label = createRichText(__assign({ key: point.name, display: point.visible, text: _this.getDataLabel(dataIndex) }, _this.getLabelAttr(point)));
            group.add(label);
        });
        return group;
    };
    DemandModel.prototype.render = function () {
        var _this = this;
        var _a = this.state, cx = _a.cx, cy = _a.cy;
        var keywordColor = this.option.keywordColor || this.getSeriesColor();
        var group = new Group({ key: this.getKey('demand') });
        if (!this.visible()) {
            return group;
        }
        group.add(this.renderRipples());
        group.add(new Circle(__assign(__assign({ key: 'keyword-node', cx: cx,
            cy: cy, radius: this.option.keywordRadius, fill: keywordColor, stroke: keywordColor }, this.getShapeStyle(this.option.keywordStyle)), { onMouseOver: function (e) {
                e.target.stopAllAnimation().animateTo(__assign({ fill: keywordColor, stroke: keywordColor }, _this.getShapeStyle(_this.option.hover.keywordStyle)));
            }, onMouseOut: function (e) {
                e.target.stopAllAnimation().animateTo(__assign({ fill: keywordColor, stroke: keywordColor }, _this.getShapeStyle(_this.option.keywordStyle)));
            } })));
        group.add(createRichText(__assign({ key: 'keyword', x: cx, y: cy, text: this.option.keyword }, this.getTextStyle(this.option.keywordTextStyle))));
        group.add(this.renderPoints());
        group.add(this.renderLabels());
        return group;
    };
    DemandModel.dependencies = [];
    return DemandModel;
}(SeriesModel));
export default DemandModel;
registerChart('demand', DemandModel);
